import router from "@/router";
import {
  linkAthlete,
  passFolderToManualIntervention,
  reportUnknownAthlet,
  switchAthlete,
} from "@/services/admin.service";
import {
  editAthleteInfo,
  getFilesList,
  getFolderById,
  getFolderCount,
  getFolderForMoveList,
  getFoldersList,
  moveEnveloppeOutOfProd,
  moveFile,
  moveFileToDiscarded,
  moveFileToUnrecognized,
  processBackup,
  regenFileFromFolder,
  replaceDuplicatedFile,
  sendAllFileToReco,
  sendEnvelopeToQueue,
  sendEnvelopeToValidation,
  sendFileToReco,
  uploadAthleteFile,
  uploadFileForBackup,
  uploadInitialFileToFolder,
  uploadReferenceFile,
} from "@/services/file.service";
import { setDescription } from "@/services/validor.service";
import {
  type Athlete,
  type Description,
  type Enveloppe,
  type File,
  type FolderCount,
} from "@/types/file.types";
import { errorHandler } from "@/utils/errorHandler";
import { FilesDescription } from "@/utils/fileDescription";
import { defineStore } from "pinia";
import { useFileFilterStore } from "./fileFilterStore";
import { useGlobalStore } from "./globalStore";

export const useFileStore = defineStore("file", {
  state: () => ({
    // athlete View
    athlete: {} as Athlete,
    referenceFile: {} as File,
    status: "",

    // File/FolderList View
    filesList: [] as File[],
    duplicatedFilesList: [] as File[],
    foldersList: [] as Enveloppe[],
    page: 1,
    limit: 20,
    totalFolder: 0,
    totalFile: 0,
    isFetching: false,

    // MoveFile Item
    totalFolderForMove: 0,
    pageForMove: 1,
    searchFileForMove: "",
    fileToMove: {} as File,
    folderToMoveIn: {} as Enveloppe,
    foldersForMoveList: [] as Enveloppe[],

    // Global File State
    search: "",
    searchFolder: "",
    searchFile: "",
    displayList: false,
    folderFileCount: {} as FolderCount,
    progress: 0,

    // search athlete name
    athletsList: [] as Athlete[],
    nobodyRecognizedFiles: [] as File[],
    discardedFiles: [] as File[],

    // duplicated files
    newDuplicatedAthlete: {} as Enveloppe,
    duplicatedFileToReplace: {} as File,

    // backup
    showBackupSucess: false,
  }),
  actions: {
    /// API CALL ///

    // Get a specific athlete enveloppe
    async getFolderById(folderId: string) {
      const globalStore = useGlobalStore();
      this.emptyStore();
      try {
        const res = await getFolderById(folderId);
        if (!res) {
          console.log("No info");
          return;
        }
        if (res.status) {
          if (globalStore.isDashboardRole && res.status !== "COMPLETED") {
            router.push("/folder-list/COMPLETED");
            return;
          }
          this.status = res.status;
        }
        this.athlete = res.athlete ?? ({} as Athlete);
        if (res.referenceFile) {
          this.referenceFile = res.referenceFile;
        }
        if (res.files) {
          this.filesList = this.orderFilesByDescription(res.files);
        }
        if (res.duplicateFiles) {
          this.duplicatedFilesList = res.duplicateFiles;
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Get list of files from a specific type with filter
    async getFilesList(filesList: string) {
      if (this.totalFile > 0 && this.filesList.length >= this.totalFile) {
        return;
      }
      const fileFilterStore = useFileFilterStore();
      let sortOrder = fileFilterStore.selectedOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedDateFilter ?? "NONE";
      let clusterId = fileFilterStore.selectedClusterFilter ?? "";
      let siteId = fileFilterStore.selectedSiteFilter ?? "";
      let kitId = fileFilterStore.selectedKitFilter ?? "";
      let processType = fileFilterStore.selectedFileProcessFilter ?? "";

      const globalStore = useGlobalStore();
      globalStore.showSkeletonLoading = true;
      try {
        const moment = filesList === "ALL" ? "initial" : "";
        const limitAll = filesList === "ALL" ? 25 : this.limit;
        const res = await getFilesList(
          filesList !== "ALL" ? filesList : "",
          limitAll,
          this.page,
          this.searchFile,
          sortOrder,
          dateFilter,
          clusterId,
          siteId,
          kitId,
          moment,
          processType,
        );
        if (!res) {
          console.log("No files info");
          return;
        }
        if (!res.items) {
          console.log("No files");
          return;
        }
        if (res.total) {
          this.totalFile = res.total;
        }
        res.items.forEach((file) => {
          this.filesList.push(file);
        });
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showSkeletonLoading = false;
      }
    },

    // Get list of Enveloppes from a specific type with filter
    async getFoldersList(foldersList: string) {
      if (this.isFetching) return;
      this.isFetching = true;
      if (this.totalFolder > 0 && this.foldersList.length >= this.totalFolder) {
        return;
      }
      const fileFilterStore = useFileFilterStore();
      let sortOrder = fileFilterStore.selectedOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedDateFilter ?? "NONE";
      let countryCode = fileFilterStore.selectedCountryFilter ?? "";
      let disciplineCode = fileFilterStore.selectedDisciplineFilter ?? "";
      let processType = fileFilterStore.selectedFileProcessFilter ?? "";
      let clusterId = fileFilterStore.selectedClusterFilter ?? "";
      let siteId = fileFilterStore.selectedSiteFilter ?? "";
      let kitId = fileFilterStore.selectedKitFilter ?? "";
      let status = fileFilterStore.selectedStatusFilter ?? "";

      const globalStore = useGlobalStore();
      globalStore.showSkeletonLoading = true;
      try {
        const res = await getFoldersList(
          foldersList,
          this.limit,
          this.page,
          this.searchFolder,
          sortOrder,
          dateFilter,
          fileFilterStore.showFolderNoFiles,
          clusterId,
          siteId,
          kitId,
          countryCode,
          disciplineCode,
          processType,
          status,
        );
        if (!res) {
          console.log("No folder info");
          return;
        }
        if (!res.items) {
          console.log("No envelopes");
          return;
        }
        if (res.total) {
          this.totalFolder = res.total;
        }
        res.items.forEach((envelope) => {
          this.foldersList.push(envelope);
        });
        if (this.foldersList.length) {
          for (const folder of this.foldersList) {
            folder.isOpen = this.displayList;
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        this.isFetching = false;
        globalStore.showSkeletonLoading = false;
      }
    },

    // get list of enveloppes which can receive files specific to the moving files process
    async getFoldersForMoveList(foldersList: string) {
      console.log(this.isFetching);

      if (this.isFetching) return;
      this.isFetching = true;
      const fileFilterStore = useFileFilterStore();
      let sortOrder =
        fileFilterStore.selectedMoveModalOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedMoveModalDateFilter ?? "NONE";

      if (
        this.totalFolderForMove > 0 &&
        this.foldersForMoveList.length >= this.totalFolderForMove
      ) {
        return;
      }
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      try {
        const res = await getFolderForMoveList(
          foldersList,
          10,
          this.pageForMove,
          this.searchFileForMove,
          sortOrder,
          dateFilter,
        );

        if (!res) {
          console.log("No folder info");
          return;
        }
        if (!res.items) {
          console.log("No envelopes");
          return;
        }
        if (res.total) {
          this.totalFolderForMove = res.total;
        }
        console.log(res.items);

        res.items.forEach((envelope) => {
          this.foldersForMoveList.push(envelope);
        });
        if (this.foldersForMoveList.length) {
          for (const folder of this.foldersForMoveList) {
            folder.isOpen = this.displayList;
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        this.isFetching = false;
        setTimeout(() => {
          globalStore.showLoader = false;
        }, 500);
      }
    },

    // get list of files from 'Nobody_recognized' specific to the moving files process
    async loadNobodyRecognizedFiles() {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      const fileFilterStore = useFileFilterStore();
      let sortOrder =
        fileFilterStore.selectedMoveModalOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedMoveModalDateFilter ?? "NONE";
      let clusterId = fileFilterStore.selectedClusterFilter ?? "";
      let siteId = fileFilterStore.selectedSiteFilter ?? "";
      let kitId = fileFilterStore.selectedKitFilter ?? "";
      try {
        const res = await getFilesList(
          "NOBODY_RECOGNIZED",
          this.limit,
          1,
          "",
          sortOrder,
          dateFilter,
          clusterId,
          siteId,
          kitId,
          "",
        );
        if (res && res.items) {
          this.nobodyRecognizedFiles = res.items;
        } else {
          this.nobodyRecognizedFiles = [];
        }
      } catch (error) {
        this.nobodyRecognizedFiles = [];
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showLoader = false;
      }
    },

    // get list of files from 'Discarded Files' spécifique au process de déplacement de fichiers
    async loadDiscardedFiles() {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      const fileFilterStore = useFileFilterStore();
      let sortOrder =
        fileFilterStore.selectedMoveModalOrderTimeFilter ?? "DESC";
      let dateFilter = fileFilterStore.selectedMoveModalDateFilter ?? "NONE";
      let clusterId = fileFilterStore.selectedClusterFilter ?? "";
      let siteId = fileFilterStore.selectedSiteFilter ?? "";
      let kitId = fileFilterStore.selectedKitFilter ?? "";
      try {
        const res = await getFilesList(
          "DISCARDED",
          this.limit,
          1,
          "",
          sortOrder,
          dateFilter,
          clusterId,
          siteId,
          kitId,
          "",
        );
        if (res && res.items) {
          this.discardedFiles = res.items;
        } else {
          this.discardedFiles = [];
        }
      } catch (error) {
        this.discardedFiles = [];
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showLoader = false;
      }
    },

    // send a file to be recognized again, if athlete on file is found, sended to athlete enveloppe
    async sendFileToReco(file: File) {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      try {
        await sendFileToReco(file.id);
        return true;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showLoader = false;
      }
    },

    // send all to reco
    async sendAllToReco() {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      try {
        await sendAllFileToReco();
        this.filesList = [];
        this.totalFile = 0;
        return true;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showLoader = false;
      }
    },

    // move a selected file to a selected enveloppe
    async moveFile(id: string) {
      try {
        await moveFile(this.fileToMove.id, id);
        this.emptyStore();
        this.fileToMove = {} as File;
        this.folderToMoveIn = {} as Enveloppe;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // move a selected file to the 'Nobody_recognized' enveloppe
    async moveFileToUnrecognized() {
      const global = useGlobalStore();
      try {
        global.showLoader = true;
        await moveFileToUnrecognized(this.fileToMove.id);
        this.emptyStore();
        this.fileToMove = {} as File;
        this.folderToMoveIn = {} as Enveloppe;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // move a selected file to the 'Discarded' enveloppe
    async moveFileToDiscarded() {
      const global = useGlobalStore();
      try {
        global.showLoader = true;
        await moveFileToDiscarded(this.fileToMove.id);
        this.emptyStore();
        this.fileToMove = {} as File;
        this.folderToMoveIn = {} as Enveloppe;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async moveOut(envelopeId: string) {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        await moveEnveloppeOutOfProd(envelopeId);
        this.getFolderById(envelopeId);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // send new file as initial file for a specific enveloppe
    async newInitialFileToUpload(
      event: Event,
      folderId: string,
      description: string,
    ): Promise<void> {
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (
        file &&
        (file.type.startsWith("image/") || file.type.startsWith("audio/"))
      ) {
        const fileToSend = new File([file], this.getFileName(description), {
          type: file.type,
          lastModified: file.lastModified,
        });
        const global = useGlobalStore();
        global.showProgressLoader = true;
        try {
          await uploadInitialFileToFolder(fileToSend, folderId, description);
          this.emptyStore();
        } catch (error) {
          if (error instanceof Error) {
            errorHandler(error);
          }
        } finally {
          global.showProgressLoader = false;
          this.progress = 0;
          if (folderId) {
            this.getFolderById(folderId);
          }
        }
      }
    },

    // Send specific enveloppe to quality control
    async sendEnvelopeToValidation(folderId: string): Promise<string | void> {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        const res = await sendEnvelopeToValidation(folderId);
        if (res && res.id) {
          return res.id;
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // Send specific enveloppe to quality control
    async sendEnvelopeToQueue(folderId: string): Promise<string | void> {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        const res = await sendEnvelopeToQueue(folderId);
        if (res) {
          this.status = res.status ?? this.status;
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // Select an initial file from an enveloppe to use in backup process
    async backupFromInitial(
      envelopeId: string,
      file: File,
    ): Promise<string | void> {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        await processBackup(envelopeId, file.id);
        this.showBackupSucess = true;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // Upload file to an enveloppe to use in backup process
    async backupFromUpload(
      envelopeId: string,
      file: any,
    ): Promise<string | void> {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        await uploadFileForBackup(file, envelopeId);
        this.showBackupSucess = true;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    async replaceDuplicatedFile(file: any): Promise<string | void> {
      const global = useGlobalStore();
      global.showLoader = true;
      try {
        await replaceDuplicatedFile(this.duplicatedFileToReplace.id, file.id);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;
      }
    },

    // Switch Enveloppe of two athletes
    async linkAthlete(folderId: string) {
      const globalStore = useGlobalStore();
      try {
        const res = await linkAthlete(
          folderId,
          globalStore.searchAthletSelected.id,
          globalStore.searchAthletSelected.disciplineCode,
        );
        if (!res || !res.athlete) {
          console.log("No info");
          return;
        }
        this.athlete = res.athlete;
        this.referenceFile = res.referenceFile ?? ({} as File);
        this.filesList = res.files
          ? this.orderFilesByDescription(res.files)
          : [];
        if (res.status) {
          this.status = res.status;
        }
        if (res.id && res.id !== folderId) {
          router.back();
          router.push(`/athlete-folder/${res.id}`);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Switch Enveloppe of two athletes
    async switchAthlete(folderId: string) {
      const globalStore = useGlobalStore();
      if (!this.athlete.id) {
        console.log("No athlete id");
        return;
      }
      try {
        const res = await switchAthlete(
          folderId,
          this.athlete.id,
          this.athlete.disciplineCode,
          globalStore.searchAthletSelected.id,
          globalStore.searchAthletSelected.disciplineCode,
        );
        if (!res || !res.athlete) {
          console.log("No info");
          return;
        }
        this.athlete = res.athlete;
        this.referenceFile = res.referenceFile ?? ({} as File);
        this.filesList = res.files
          ? this.orderFilesByDescription(res.files)
          : [];
        if (res.status) {
          this.status = res.status;
        }
        if (res.id && res.id !== folderId) {
          router.push(`/athlete-folder/${res.id}`);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    // Report an athlete as an unknow athlet
    async reportUnknownAthlet(envId: string) {
      try {
        const res = await reportUnknownAthlet(envId);
        if (!res) {
          console.log("No info");
          return;
        }
        this.athlete = {} as Athlete;
        this.referenceFile = res.referenceFile ?? ({} as File);
        this.filesList = res.files
          ? this.orderFilesByDescription(res.files)
          : [];
        if (res.status) {
          this.status = res.status;
        }
        if (res.id && res.id !== envId) {
          router.push(`/athlete-folder/${res.id}`);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },

    async editAthleteInfo(
      editableValues: {
        countryCode: string;
        firstName: string;
        lastName: string;
      },
      oldDisciplineCode: string,
    ) {
      const globalStore = useGlobalStore();
      try {
        globalStore.showLoader = true;
        const res = await editAthleteInfo(
          editableValues,
          this.athlete.id,
          oldDisciplineCode,
        );
        if (!res) {
          console.log("No info");
          return;
        }
        this.athlete = res;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        globalStore.showLoader = false;
      }
    },

    // Upload file to serve as Reference File for an Athlete, file needs to be correctly named
    async referenceFileToUpload(event: Event): Promise<any> {
      const input = event.target as HTMLInputElement;
      const files = input?.files;
      if (!files || files.length === 0) return null;
      const results = {
        successful: [] as any[],
        failed: [] as any[],
      };

      try {
        const batchSize = 20;
        for (let i = 0; i < files.length; i += batchSize) {
          const batch = Array.from(files).slice(i, i + batchSize);
          const res = (await uploadReferenceFile(batch)) as {
            data: {
              successful?: any[];
              failed?: any[];
            };
          };
          if (res?.data.successful) {
            results.successful.push(...res.data.successful);
          }
          if (res?.data.failed) {
            results.failed.push(...res.data.failed);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        this.progress = 0;
      }
      return results;
    },

    // Upload file to serve as Reference File for an Athlete, file needs to be correctly named
    async uploadAthletesFiles(
      event: Event,
      mode: "classic" | "backup",
    ): Promise<any> {
      const input = event.target as HTMLInputElement;
      const files = input?.files;
      if (!files || files.length === 0) return null;
      const results = {
        successful: [] as any[],
        failed: [] as any[],
      };

      try {
        const batchSize = 10;
        for (let i = 0; i < files.length; i += batchSize) {
          const batch = Array.from(files).slice(i, i + batchSize);
          const res = (await uploadAthleteFile(batch, mode)) as {
            data: {
              successful?: any[];
              failed?: any[];
            };
          };
          if (res?.data.successful) {
            results.successful.push(...res.data.successful);
          }
          if (res?.data.failed) {
            results.failed.push(...res.data.failed);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        this.progress = 0;
      }
      return results;
    },

    // if all initial files are in enveloppes, generate new generated files
    async regenFiles(id: string) {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      try {
        await regenFileFromFolder(id);
        this.getFolderById(id);
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        setTimeout(() => {
          globalStore.showLoader = false;
        }, 500);
      }
    },

    async passFolderToManualIntervention(folderId: string) {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;
      try {
        const res = await passFolderToManualIntervention(folderId);
        if (!res || !res.athlete) {
          console.log("No info");
          return;
        }
        this.athlete = res.athlete;
        this.referenceFile = res.referenceFile ?? ({} as File);
        this.filesList = res.files
          ? this.orderFilesByDescription(res.files)
          : [];
        if (res.status) {
          this.status = res.status;
        }
        if (res.id && res.id !== folderId) {
          router.push(`/athlete-folder/${res.id}`);
        }
        if (res.id && res.id !== folderId) {
          router.push(`/athlete-folder/${res.id}`);
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        setTimeout(() => {
          globalStore.showLoader = false;
        }, 1000);
      }
    },

    async setDescription(fileId: string, description: string) {
      const globalStore = useGlobalStore();
      globalStore.showLoader = true;

      if (!description) {
        console.log("No file to switch");
        return;
      }
      try {
        await setDescription(fileId, description);
        this.emptyStore();
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        setTimeout(() => {
          globalStore.showLoader = false;
        }, 1000);
      }
    },

    /// FILES INFO LOGIC ///

    getFileOrigin(file: File) {
      if (!file.description) {
        return;
      }
      if (file.description.moment === "initial") {
        return "files-short-description.original";
      } else {
        return "files-short-description.generated";
      }
    },
    getFileShortDesc(file: File) {
      if (!file.description) {
        return;
      }
      const matchingKey = Object.entries(FilesDescription).find(
        ([key, value]) =>
          value === this.getFileDescriptionKey(file.description),
      );
      if (!matchingKey) {
        return `files-short-description.default`;
      }
      const i18nKey = matchingKey[0].toLowerCase();
      return `files-short-description.${i18nKey}`;
    },
    getFileDescriptionKey(description: Description) {
      return `${description?.moment || ""}:${description?.step || ""}:${
        description?.type || ""
      }:${description?.bgType || ""}:${description?.processType || ""}`;
    },
    async getFolderCount() {
      try {
        const res = await getFolderCount();
        if (!res) {
          console.log("No folder count");
          return;
        }
        this.folderFileCount = res;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    },
    getFileName(desc: string) {
      switch (desc) {
        case FilesDescription.Formal_With_Accreditation_With_Background:
          return `${this.athlete.id}_${this.athlete.firstName}_${this.athlete.lastName}_accred.jpg`;
        case FilesDescription.Formal_With_Background:
          return `${this.athlete.id}_${this.athlete.firstName}_${this.athlete.lastName}_formal.jpg`;
        case FilesDescription.Athlete_Saying_Name:
          return `${this.athlete.id}_${this.athlete.firstName}_${this.athlete.lastName}_formal.wav`;
        case FilesDescription.Informal_With_Background:
          return `${this.athlete.id}_${this.athlete.firstName}_${this.athlete.lastName}_informal.jpg`;
        case FilesDescription.Empty_Audio:
          return `${this.athlete.id}_${this.athlete.firstName}_${this.athlete.lastName}_informal.wav`;
        default:
          throw new Error("Unsupported description");
      }
    },

    /// FILES MANAGEMENT VIEW LOGIC ///

    emptyStore() {
      this.athlete = {} as Athlete;
      this.referenceFile = {} as File;
      this.status = "";
      this.foldersList = [] as Enveloppe[];
      this.filesList = [] as File[];
      this.search = "";
      this.searchFileForMove = "";
      this.totalFile = 0;
      this.totalFolder = 0;
      this.page = 1;
      this.folderToMoveIn = {} as Enveloppe;
      this.athletsList = [] as Athlete[];
      this.duplicatedFileToReplace = {} as File;
      this.duplicatedFilesList = [] as File[];
    },
    toggleFolder(folderId: string) {
      const folder = this.foldersList.find((f) => f.id === folderId);
      if (folder) {
        folder.isOpen = !folder.isOpen;
      }
    },
    openFolders() {
      this.foldersList.forEach((folder) => {
        folder.isOpen = true;
      });
    },
    closeFolders() {
      this.foldersList.forEach((folder) => {
        folder.isOpen = false;
      });
    },
    orderFilesByDescription(list: File[]) {
      return list;
    },

    folderFiltered(folderFiles: File[]) {
      return folderFiles
        .filter(
          (file) =>
            (!file.description || file.description.moment === "initial") &&
            (!file.description || file.description.step !== "reference"),
        )
        .sort((a, b) => {
          // Trier les images en priorité
          if (a.type === "image" && b.type !== "image") return -1;
          if (a.type !== "image" && b.type === "image") return 1;

          // Trier les images selon l'ordre donné
          const imagePriority = (file: File) => {
            if (
              this.getFileDescriptionKey(file.description).includes(
                "accreditation",
              )
            )
              return 1;
            if (
              this.getFileDescriptionKey(file.description).includes(
                "initial:formal",
              )
            )
              return 2;
            if (
              this.getFileDescriptionKey(file.description).includes(
                "initial:informal",
              )
            )
              return 3;
            return 4; // Par défaut, les autres images vont en dernier
          };

          // Trier les audios selon l'ordre donné
          const audioPriority = (file: File) => {
            if (this.getFileDescriptionKey(file.description).includes("saying"))
              return 1;
            if (this.getFileDescriptionKey(file.description).includes("empty"))
              return 2;
            return 3; // Par défaut, les autres audios vont en dernier
          };

          if (a.type === "image" && b.type === "image") {
            return imagePriority(a) - imagePriority(b);
          }

          if (a.type === "audio" && b.type === "audio") {
            return audioPriority(a) - audioPriority(b);
          }

          return 0;
        });
    },
  },
});
